import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/Contact.css';
import { FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaPhone, FaClock, FaCheck } from 'react-icons/fa';

const Contact = () => {
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        service: 'General Inquiry'
    });

    // Form validation state
    const [errors, setErrors] = useState({});

    // Success message state
    const [showSuccess, setShowSuccess] = useState(false);

    // Loading state
    const [isLoading, setIsLoading] = useState(false);

    // Reference for the contact page
    const contactRef = useRef(null);

    // Services options
    const services = [
        'General Inquiry',
        'Domestic Tour Package',
        'International Tour Package',
        'Hajj & Umrah Package',
        'Cruise Vacation',
        'Car Rental',
        'Travel Insurance',
        'Passport & Visa Services'
    ];

    // Handle input change
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });

        // Clear error when user types
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: null
            });
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[0-9]{10,}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
            newErrors.phone = 'Phone number is invalid';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsLoading(true);

            // Simulate API call
            setTimeout(() => {
                setIsLoading(false);
                setShowSuccess(true);

                // Prepare WhatsApp message
                const whatsappNumber = "919893020271"; // Replace with your WhatsApp number
                const message = encodeURIComponent(
                    `*New Inquiry from Website*\n\n` +
                    `*Name:* ${formData.name}\n` +
                    `*Email:* ${formData.email}\n` +
                    `*Phone:* ${formData.phone}\n` +
                    `*Service:* ${formData.service}\n` +
                    `*Subject:* ${formData.subject}\n\n` +
                    `*Message:*\n${formData.message}`
                );

                // Open WhatsApp with the message
                window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');

                // Reset form after 3 seconds
                setTimeout(() => {
                    setShowSuccess(false);
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        subject: '',
                        message: '',
                        service: 'General Inquiry'
                    });
                }, 3000);
            }, 1500);
        } else {
            // Scroll to the first error
            const firstError = Object.keys(errors)[0];
            if (firstError) {
                document.getElementById(firstError).focus();
            }
        }
    };

    // Animation variants for staggered animations

    const staggerContainer = {
        initial: { opacity: 0 },
        animate: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariant = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 }
    };

    return (
        <div className="contact-page" ref={contactRef}>
            {/* Fixed Background */}
            <div className="contact-background">
                <div className="contact-overlay"></div>
            </div>

            {/* Hero Section */}
            <motion.div
                className="contact-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <motion.h1
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    Get In Touch
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                >
                    We're here to help plan your perfect journey
                </motion.p>

                <motion.div
                    className="contact-cards"
                    variants={staggerContainer}
                    initial="initial"
                    animate="animate"
                >
                    <motion.a
                        href="tel:+919893020271"
                        className="contact-card"
                        variants={itemVariant}
                        whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0,0,0,0.2)" }}
                    >
                        <div className="contact-card-icon">
                            <FaPhone />
                        </div>
                        <h3>Call Us</h3>
                        <p>+91 9893020271</p>
                    </motion.a>

                    <motion.a
                        href="https://wa.me/919893020271"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-card"
                        variants={itemVariant}
                        whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0,0,0,0.2)" }}
                    >
                        <div className="contact-card-icon whatsapp">
                            <FaWhatsapp />
                        </div>
                        <h3>WhatsApp</h3>
                        <p>Chat with us</p>
                    </motion.a>

                    <motion.a
                        href="mailto:info@excellenttravels.co.in"
                        className="contact-card"
                        variants={itemVariant}
                        whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0,0,0,0.2)" }}
                    >
                        <div className="contact-card-icon email">
                            <FaEnvelope />
                        </div>
                        <h3>Email</h3>
                        <p>Send us a message</p>
                    </motion.a>
                </motion.div>
            </motion.div>

            <div className="contact-container">
                <motion.div
                    className="contact-info-section"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <h2>Contact Information</h2>
                    <div className="contact-details">
                        <div className="contact-item">
                            <div className="icon">
                                <FaMapMarkerAlt />
                            </div>
                            <div className="info">
                                <h3>Address</h3>
                                <p>UG-29, Central Market, Opp. Bapu Ki Kutia</p>
                                <p>Roshanpura, Bhopal-462003. (M.P.) India</p>
                            </div>
                        </div>

                        <div className="contact-item">
                            <div className="icon">
                                <FaEnvelope />
                            </div>
                            <div className="info">
                                <h3>Email</h3>
                                <p>info@excellenttravels.co.in</p>
                                <p>sajidali3273@gmail.com</p>
                            </div>
                        </div>

                        <div className="contact-item">
                            <div className="icon">
                                <FaPhone />
                            </div>
                            <div className="info">
                                <h3>Phone</h3>
                                <p>+91 9893020271</p>
                                <p>+91 9399418415</p>
                            </div>
                        </div>

                        <div className="contact-item">
                            <div className="icon">
                                <FaPhone />
                            </div>
                            <div className="info">
                                <h3>Telephone</h3>
                                <p>0755-4220722</p>
                            </div>
                        </div>

                        <div className="contact-item">
                            <div className="icon">
                                <FaClock />
                            </div>
                            <div className="info">
                                <h3>Business Hours</h3>
                                <p>Monday - Saturday: 10:00 AM - 7:00 PM</p>
                                <p>Sunday: Closed</p>
                            </div>
                        </div>
                    </div>

                    <div className="social-media-section">
                        <h3>Connect With Us</h3>
                        <div className="social-icons">
                            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon facebook">
                                <i className="fab fa-facebook-f"></i>
                            </a>
                            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon instagram">
                                <i className="fab fa-instagram"></i>
                            </a>
                            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-icon twitter">
                                <i className="fab fa-twitter"></i>
                            </a>
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon linkedin">
                                <i className="fab fa-linkedin-in"></i>
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="contact-form-section"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <h2>Send Us a Message</h2>
                    <p className="form-subtitle">Complete the form below with your travel requirements, and our expert team will assist you promptly.</p>

                    <AnimatePresence>
                        {showSuccess && (
                            <motion.div
                                className="success-message"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                            >
                                <FaCheck />
                                <p>Your message has been sent successfully!</p>
                                <p className="success-subtitle">We'll get back to you soon.</p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form className="contact-form" onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="name">Full Name*</label>
                                <input
                                    type="text"
                                    id="name"
                                    placeholder="Your Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={errors.name ? 'error' : ''}
                                    required
                                />
                                {errors.name && <span className="error-message">{errors.name}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address*</label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder="Your Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={errors.email ? 'error' : ''}
                                    required
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="phone">Phone Number*</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    placeholder="Your Phone Number"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={errors.phone ? 'error' : ''}
                                    required
                                />
                                {errors.phone && <span className="error-message">{errors.phone}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="service">Service Interested In</label>
                                <select
                                    id="service"
                                    value={formData.service}
                                    onChange={handleChange}
                                >
                                    {services.map((service, index) => (
                                        <option key={index} value={service}>{service}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="subject">Subject</label>
                            <input
                                type="text"
                                id="subject"
                                placeholder="Subject"
                                value={formData.subject}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">Message*</label>
                            <textarea
                                id="message"
                                rows="5"
                                placeholder="Your Message"
                                value={formData.message}
                                onChange={handleChange}
                                className={errors.message ? 'error' : ''}
                                required
                            ></textarea>
                            {errors.message && <span className="error-message">{errors.message}</span>}
                        </div>

                        <div className="form-footer">
                            <p className="privacy-note">
                                By submitting this form, you agree to our <Link to="/privacy-policy">Privacy Policy</Link>.
                            </p>

                            <motion.button
                                type="submit"
                                className={`submit-button ${isLoading ? 'loading' : ''}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                disabled={isLoading || showSuccess}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        <span>Sending...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Send via WhatsApp</span>
                                        <FaWhatsapp className="whatsapp-icon" />
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </form>
                </motion.div>
            </div>

            <motion.div
                className="map-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
            >
                <h2>Find Us</h2>
                <div className="map-container">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.2093742113036!2d77.4320863!3d23.2332408!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c4268d2c0a5a5%3A0x4f4a72d9c0af13bd!2sCentral%20Market%2C%20Roshanpura%2C%20Bhopal%2C%20Madhya%20Pradesh%20462003!5e0!3m2!1sen!2sin!4v1650000000000!5m2!1sen!2sin"
                        width="100%"
                        height="450"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Excellent Travels Location"
                    ></iframe>
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
            >
                <h2>Frequently Asked Questions</h2>
                <div className="faq-container">
                    <div className="faq-item">
                        <h3>How do I book a tour package?</h3>
                        <p>You can book a tour package by contacting us through phone, email, or by filling out the contact form on this page. Our team will get back to you with all the necessary information and guide you through the booking process.</p>
                    </div>

                    <div className="faq-item">
                        <h3>What payment methods do you accept?</h3>
                        <p>We accept various payment methods including credit/debit cards, bank transfers, and cash payments at our office. For international bookings, we also accept PayPal and wire transfers.</p>
                    </div>

                    <div className="faq-item">
                        <h3>Can I customize my tour package?</h3>
                        <p>Yes, we offer customized tour packages tailored to your preferences and requirements. Contact us with your specific needs, and our travel experts will create a personalized itinerary for you.</p>
                    </div>

                    <div className="faq-item">
                        <h3>What is your cancellation policy?</h3>
                        <p>Our cancellation policy varies depending on the type of booking and the time of cancellation. Please contact us for specific details regarding your booking.</p>
                    </div>
                </div>
            </motion.div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
            >
                <div className="cta-content">
                    <h2>Ready to Start Your Journey?</h2>
                    <p>Contact us today and let our travel experts help you plan your perfect trip.</p>
                    <div className="cta-buttons">
                        <Link to="/services" className="cta-button primary">
                            Explore Our Services
                            <span className="arrow-icon">→</span>
                        </Link>
                        <a href="tel:+919893020271" className="cta-button secondary">
                            Call Us Now
                        </a>
                    </div>
                </div>
            </motion.div>


        </div>
    );
};

export default Contact;
